export type CanvasDesignChild = {
  canvasId: string;
  designId: string;
  label: string;
  isDefault?: boolean;
};

export type CanvasDesignTreeNode = CanvasDesignChild & {
  collapsed: boolean;
  children?: CanvasDesignChild[];
};

export type ArchivedScopeRow = {
  kind: 'canvas' | 'design';
  canvasId: string;
  designId: string;
  label: string;
};

export type CanvasDesignTreeMode =
  | 'all-active-designs'
  | 'active-canvases-only';

type CanvasRow = {
  id?: string;
  name?: string;
  is_default?: boolean;
  default_design_id?: string | null;
  archived_at?: string | null;
  designs?: Array<{
    id?: string;
    name?: string;
    is_default?: boolean;
    archived_at?: string | null;
  }>;
};

function activeDesigns(canvas: CanvasRow) {
  return (canvas.designs || []).filter((row) => !row.archived_at);
}

export function countActiveDesigns(
  canvases: CanvasRow[] | null | undefined,
  canvasId: string
): number {
  const canvas = (canvases || []).find(
    (row) => String(row.id || '') === String(canvasId)
  );
  return canvas && !canvas.archived_at ? activeDesigns(canvas).length : 0;
}

function resolveDefaultDesignId(canvas: CanvasRow): string {
  const canvasId = String(canvas.id || '');
  if (canvas.default_design_id) {
    return String(canvas.default_design_id);
  }
  const designs = activeDesigns(canvas);
  const identity =
    designs.find((row) => String(row.id) === canvasId) ||
    designs.find((row) => row.is_default) ||
    designs[0];
  return String(identity?.id || canvasId);
}

export function resolveArchiveTarget(
  canvasId: string,
  designId: string,
  canvases?: CanvasRow[] | null
): 'canvas' | 'design' {
  const canvas = (canvases || []).find(
    (row) => String(row.id || '') === String(canvasId)
  );
  if (canvas) {
    const designs = activeDesigns(canvas);
    return designs.length > 1 ? 'design' : 'canvas';
  }
  return String(designId) !== String(canvasId) ? 'design' : 'canvas';
}

export function formatCanvasDesignHeading(
  canvasName?: string | null,
  designName?: string | null
): string {
  const canvas = String(canvasName ?? '').trim();
  const design = String(designName ?? '').trim();
  if (canvas && design && canvas !== design) return `${canvas} · ${design}`;
  return design || canvas;
}

export function headingForDesign(
  canvases: CanvasRow[] | null | undefined,
  designId: string
): string {
  const key = String(designId || '').trim();
  if (!key) return '';
  for (const canvas of canvases || []) {
    const matched = (canvas.designs || []).find(
      (row) => String(row.id || '') === key
    );
    if (matched) {
      return formatCanvasDesignHeading(canvas.name, matched.name);
    }
  }
  for (const canvas of canvases || []) {
    if (String(canvas.id || '') === key) {
      return formatCanvasDesignHeading(canvas.name, canvas.name);
    }
  }
  return '';
}

export function resolveScopeFromDesignId(
  canvases: CanvasRow[] | null | undefined,
  designKey: string
): { canvasId: string; designId: string } | null {
  const key = String(designKey || '').trim();
  if (!key) return null;
  for (const canvas of canvases || []) {
    const canvasId = String(canvas.id || '');
    if (!canvasId) continue;
    if (canvasId === key) {
      const identity = (canvas.designs || []).find(
        (row) => String(row.id || '') === key
      );
      if (identity) {
        return { canvasId, designId: key };
      }
      return { canvasId, designId: resolveDefaultDesignId(canvas) || canvasId };
    }
    for (const row of canvas.designs || []) {
      if (String(row.id) === key) {
        return { canvasId, designId: key };
      }
    }
  }
  return null;
}

export function shapeCanvasDesignTree(
  canvases: CanvasRow[] | null | undefined,
  mode: CanvasDesignTreeMode = 'all-active-designs'
): CanvasDesignTreeNode[] {
  const nodes: CanvasDesignTreeNode[] = [];
  for (const canvas of canvases || []) {
    if (canvas.archived_at) continue;
    const canvasId = String(canvas.id || '');
    if (!canvasId) continue;
    const designs = activeDesigns(canvas);
    const designId = resolveDefaultDesignId(canvas);
    const canvasLabel =
      (canvas.name && String(canvas.name).trim()) ||
      `Canvas ${canvasId.slice(0, 8)}`;

    if (mode === 'active-canvases-only') {
      nodes.push({
        canvasId,
        designId: designId || canvasId,
        label: canvasLabel,
        isDefault: Boolean(canvas.is_default),
        collapsed: true
      });
      continue;
    }

    if (!designs.length) {
      if ((canvas.designs || []).length > 0) continue;
      nodes.push({
        canvasId,
        designId: canvasId,
        label: canvasLabel,
        isDefault: Boolean(canvas.is_default),
        collapsed: true
      });
      continue;
    }
    if (designs.length === 1) {
      const only = designs[0];
      const designLabel =
        (only.name && String(only.name).trim()) ||
        `Design ${String(only.id).slice(0, 8)}`;
      const combined =
        canvasLabel === designLabel
          ? canvasLabel
          : `${canvasLabel} · ${designLabel}`;
      nodes.push({
        canvasId,
        designId: String(only.id),
        label: combined,
        isDefault: Boolean(only.is_default || canvas.is_default),
        collapsed: true
      });
      continue;
    }
    nodes.push({
      canvasId,
      designId: designId || canvasId,
      label: canvasLabel,
      isDefault: Boolean(canvas.is_default),
      collapsed: false,
      children: designs.map((row) => ({
        canvasId,
        designId: String(row.id),
        label:
          (row.name && String(row.name).trim()) ||
          `Design ${String(row.id).slice(0, 8)}`,
        isDefault: Boolean(row.is_default)
      }))
    });
  }
  return nodes;
}

export function pickNextActiveScope(
  canvases: CanvasRow[] | null | undefined,
  mode: CanvasDesignTreeMode = 'all-active-designs'
): { canvasId: string; designId: string } | null {
  const activeRows = (canvases || []).filter((row) => !row?.archived_at);
  const tree = shapeCanvasDesignTree(activeRows, mode);
  if (!tree.length) return null;
  const preferredCanvas =
    activeRows.find((item) => item?.is_default && !item?.archived_at) ??
    activeRows.find((item) => !item?.archived_at);
  if (preferredCanvas) {
    const node = tree.find(
      (entry) => String(entry.canvasId) === String(preferredCanvas.id)
    );
    if (node) {
      return { canvasId: node.canvasId, designId: node.designId };
    }
  }
  const first = tree[0];
  return { canvasId: first.canvasId, designId: first.designId };
}

export function collectArchivedScopes(
  canvases: CanvasRow[] | null | undefined
): ArchivedScopeRow[] {
  const rows: ArchivedScopeRow[] = [];
  for (const canvas of canvases || []) {
    const canvasId = String(canvas.id || '');
    if (!canvasId) continue;
    if (canvas.archived_at) {
      rows.push({
        kind: 'canvas',
        canvasId,
        designId: resolveDefaultDesignId(canvas) || canvasId,
        label:
          (canvas.name && String(canvas.name).trim()) ||
          `Canvas ${canvasId.slice(0, 8)}`
      });
      continue;
    }
    for (const design of canvas.designs || []) {
      if (!design?.archived_at) continue;
      const designId = String(design.id || '');
      if (!designId) continue;
      rows.push({
        kind: 'design',
        canvasId,
        designId,
        label:
          (design.name && String(design.name).trim()) ||
          `Design ${designId.slice(0, 8)}`
      });
    }
  }
  return rows;
}

export function formatScopeOptionValue(canvasId: string, designId: string) {
  return `${canvasId}:${designId}`;
}

export function parseScopeOptionValue(value: string) {
  const raw = String(value || '');
  const idx = raw.indexOf(':');
  if (idx < 0) return null;
  const canvasId = raw.slice(0, idx);
  const designId = raw.slice(idx + 1);
  if (!canvasId || !designId) return null;
  return { canvasId, designId };
}

export function formatArchivedScopeValue(row: ArchivedScopeRow) {
  return `${row.kind}:${row.canvasId}:${row.designId}`;
}

export function parseArchivedScopeValue(
  value: string
): { kind: 'canvas' | 'design'; canvasId: string; designId: string } | null {
  const raw = String(value || '');
  const first = raw.indexOf(':');
  const second = raw.indexOf(':', first + 1);
  if (first < 0 || second < 0) return null;
  const kind = raw.slice(0, first);
  if (kind !== 'canvas' && kind !== 'design') return null;
  const canvasId = raw.slice(first + 1, second);
  const designId = raw.slice(second + 1);
  if (!canvasId || !designId) return null;
  return { kind, canvasId, designId };
}
